import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { getSubscriptionPlans, getMySubscription, createCheckout, getStripePublicConfig, getSettings } from '../lib/api';
import { redirectToPayment } from '../lib/paymentRedirect';
import Footer from '../components/Footer';
import InlineEditable from '../components/InlineEditable';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import PromoCodeInput from '../components/PromoCodeInput';
import PayWithCryptoButton from '../components/PayWithCryptoButton';
import { toast } from 'sonner';
import { Crown, Check, Sparkles, Star, Zap, Gift, ArrowDownRight } from 'lucide-react';

export default function VipPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [subscribing, setSubscribing] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        // First check if VIP section is enabled (admin can disable from settings)
        try {
          const settingsRes = await getSettings();
          const flags = settingsRes.data?.settings?.feature_flags || {};
          if (flags.nav_vip === false) {
            // VIP disabled — silently redirect to home
            navigate('/', { replace: true });
            return;
          }
        } catch {
          // ignore — settings fetch failure shouldn't break the page
        }
        const [p, s, sp] = await Promise.all([
          getSubscriptionPlans(),
          user ? getMySubscription() : Promise.resolve({ data: { subscription: null } }),
          getStripePublicConfig(),
        ]);
        setPlans(p.data.plans || []);
        setSubscription(s.data.subscription);
        setStripeEnabled(sp.data?.enabled === true);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [user, navigate]);

  const handleSubscribe = async (planId) => {
    if (!user) { navigate('/login'); return; }
    if (!stripeEnabled) {
      toast.info(t('stripeComingSoon') || 'Paiements bientôt disponibles');
      return;
    }
    setSubscribing(planId);
    try {
      const res = await createCheckout({
        type: 'vip',
        plan_id: planId,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || undefined,
      });
      const ok = await redirectToPayment(res.data);
      if (!ok) { toast.error('URL de paiement manquante'); setSubscribing(null); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de paiement');
      setSubscribing(null);
    }
  };

  const planTranslations = {
    vip_monthly: {
      name: t('vipMonthlyName'),
      benefits: [t('vipBenefitMonthly1'), t('vipBenefitPriority'), t('vipBenefitExclusive'), t('vipBenefitBonus10')],
    },
    vip_yearly: {
      name: t('vipAnnualName'),
      benefits: [t('vipBenefitAnnual1'), t('vipBenefitPriority'), t('vipBenefitExclusive'), t('vipBenefitBonus15'), t('vipBenefitBirthday')],
    },
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-rolex-green"><div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const icons = [Crown, Star];

  return (
    <div className="min-h-screen bg-rolex-green">
      {/* Hero */}
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 hero-spotlight pointer-events-none" />
        <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gold-gradient mb-6 shadow-[0_8px_32px_rgba(201,169,97,0.4)]">
            <Crown className="w-10 h-10 text-rolex-green" strokeWidth={2} />
          </div>
          <p className="gold-line mb-4 mx-auto justify-center"><Sparkles className="w-3 h-3" /> <InlineEditable tKey="vipExclusive">{t('vipExclusive')}</InlineEditable></p>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight mb-4 text-cream">
            <span className="text-gold-gradient"><InlineEditable tKey="vipTitle">{t('vipTitle')}</InlineEditable></span>
          </h1>
          <p className="text-cream/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed font-light"><InlineEditable tKey="vipDesc">{t('vipDesc')}</InlineEditable></p>
        </div>
      </section>

      <section className="pb-20 sm:pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {subscription && (
            <div className="rounded-2xl bg-gold-gradient p-[1.5px] mb-10 gold-glow-strong">
              <div className="rounded-2xl bg-rolex-green p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gold-gradient flex items-center justify-center"><Crown className="w-7 h-7 text-rolex-green" /></div>
                <div>
                  <p className="font-display font-bold text-xl text-cream"><InlineEditable tKey="yourSubscription">{t('yourSubscription')}</InlineEditable></p>
                  <p className="text-sm text-gold">{subscription.plan_name} — <InlineEditable tKey="active">{t('active')}</InlineEditable></p>
                </div>
              </div>
            </div>
          )}

          {!subscription?.active && user && (
            <div className="mb-8 max-w-xl mx-auto">
              <PromoCodeInput
                variant="dark"
                applied={appliedPromo}
                onApplied={setAppliedPromo}
                context={{}}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {plans.map((plan, idx) => {
              const Icon = icons[idx] || Crown;
              const isPopular = idx === 0;
              const pt = planTranslations[plan.id] || {};
              const isCurrentPlan = subscription?.active && (subscription.plan_id === plan.id || subscription.plan_id === plan._id);
              const userHasOtherSub = subscription?.active && !isCurrentPlan;
              return (
                <div key={plan.id} className={`relative rounded-2xl p-8 card-glass card-premium-hover ${isCurrentPlan ? 'ring-2 ring-emerald-500 shadow-emerald-500/20 shadow-2xl' : isPopular ? 'gold-glow-strong' : ''}`}>
                  {isCurrentPlan && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white border-none z-10 text-[10px] font-bold tracking-widest uppercase px-3 py-1">
                      <Check className="w-3 h-3 mr-1 inline" /> <InlineEditable tKey="currentPlan">{t('currentPlan') || 'Plan actuel'}</InlineEditable>
                    </Badge>
                  )}
                  {!isCurrentPlan && isPopular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-gradient text-rolex-green border-none z-10 text-[10px] font-bold tracking-widest uppercase px-3 py-1"><InlineEditable tKey="mostPopular">{t('mostPopular')}</InlineEditable></Badge>}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-14 h-14 rounded-xl bg-gold-gradient flex items-center justify-center">
                      <Icon className="w-7 h-7 text-rolex-green" />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl font-extrabold text-cream">{pt.name || plan.name}</h3>
                      <p className="text-xs text-gold/80 uppercase tracking-wider font-semibold">{plan.points_per_month} <InlineEditable tKey="ptsPerMonth">{t('ptsPerMonth')}</InlineEditable></p>
                    </div>
                  </div>

                  <div className="mb-7 flex items-baseline gap-2">
                    <span className="font-mono text-5xl font-extrabold text-gold-gradient">£{plan.price_gbp}</span>
                    <span className="text-cream/50 text-sm">/{plan.id.includes('yearly') ? t('year') : t('month')}</span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {(pt.benefits || plan.benefits || []).map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-cream/85">
                        <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan ? (
                    <Button
                      className="w-full rounded-xl h-14 text-sm font-bold tracking-[0.12em] uppercase bg-emerald-500/20 border border-emerald-500 text-emerald-300 cursor-default hover:bg-emerald-500/30"
                      disabled
                      data-testid={`current-plan-${plan.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      <InlineEditable tKey="alreadyActive">{t('alreadyActive') || 'Déjà actif'}</InlineEditable>
                    </Button>
                  ) : userHasOtherSub ? (
                    <Button
                      className="w-full rounded-xl h-14 text-sm font-bold tracking-[0.12em] uppercase bg-amber-500/20 border border-amber-500 text-amber-300 hover:bg-amber-500/30"
                      onClick={() => {
                        toast.info(t('cancelFirstHint') || 'Annulez d\'abord votre abonnement actuel depuis votre compte avant de souscrire à un autre plan.');
                        setTimeout(() => navigate('/account'), 1500);
                      }}
                      data-testid={`switch-plan-${plan.id}`}
                    >
                      <ArrowDownRight className="w-4 h-4 mr-1" />
                      <InlineEditable tKey="cancelToSwitch">{t('cancelToSwitch') || 'Annuler pour changer'}</InlineEditable>
                    </Button>
                  ) : (
                    <>
                      <Button
                        className={`w-full rounded-xl btn-press h-14 text-sm font-bold tracking-[0.12em] uppercase ${isPopular ? 'btn-gold' : 'border border-[hsl(var(--gold)/0.4)] bg-transparent text-cream hover:bg-[hsl(var(--gold)/0.10)]'}`}
                        onClick={() => handleSubscribe(plan._id || plan.id)}
                        disabled={subscribing === (plan._id || plan.id)}
                        data-testid={`subscribe-vip-${plan._id || plan.id}`}
                      >
                        {subscribing === (plan._id || plan.id) ? '...' : <InlineEditable tKey="subscribe">{t('subscribe')}</InlineEditable>}
                      </Button>
                      <PayWithCryptoButton
                        amountGbp={plan.price_gbp}
                        payload={{ type: 'vip', plan_id: plan._id || plan.id }}
                        className="w-full mt-2 rounded-xl text-xs uppercase tracking-wider font-bold"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Benefits */}
          <div className="mt-20 text-center">
            <p className="gold-line mb-4 mx-auto justify-center"><InlineEditable tKey="vipBenefitsTitle">{t('vipBenefitsTitle')}</InlineEditable></p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-10 text-cream"><InlineEditable tKey="exclusivePerks">{t('exclusivePerks') || 'Exclusive Perks'}</InlineEditable></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: Gift, title: t('vipBenefit1Title'), desc: t('vipBenefit1Desc') },
                { icon: Zap, title: t('vipBenefit2Title'), desc: t('vipBenefit2Desc') },
                { icon: Star, title: t('vipBenefit3Title'), desc: t('vipBenefit3Desc') },
                { icon: Crown, title: t('vipBenefit4Title'), desc: t('vipBenefit4Desc') },
              ].map((b, i) => (
                <div key={i} className="rounded-2xl card-glass p-6 hover:border-[hsl(var(--gold)/0.45)] transition-colors text-center">
                  <div className="w-12 h-12 rounded-xl bg-gold-gradient flex items-center justify-center mx-auto mb-4 shadow-[0_4px_18px_rgba(201,169,97,0.30)]">
                    <b.icon className="w-6 h-6 text-rolex-green" />
                  </div>
                  <h4 className="font-display font-bold text-base text-cream mb-2">{b.title}</h4>
                  <p className="text-xs text-cream/60 font-light leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
